import React, { useEffect, useState } from 'react';
import { getProfessorMetrics } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { DollarSign, Users, BookOpen, Inbox, TrendingUp } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ProfessorMetricsBlock = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProfessorMetrics()
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-muted-foreground">Cargando métricas...</div>;
  if (!data) return null;

  const stats = [
    { key: 'courses', label: 'Cursos activos', icon: BookOpen, value: data.total_courses, color: 'text-primary' },
    { key: 'students', label: 'Estudiantes inscritos', icon: Users, value: data.total_students, color: 'text-blue-600' },
    { key: 'revenue', label: 'Total recaudado', icon: DollarSign, value: fmtUSD(data.total_revenue_usd), color: 'text-green-600' },
    { key: 'pending', label: 'Solicitudes pendientes', icon: Inbox, value: data.total_pending_requests, color: 'text-amber-600' },
  ];

  return (
    <div className="space-y-6" data-testid="professor-metrics-block">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map(s => (
          <Card key={s.key} data-testid={`metric-${s.key}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <s.icon className={`w-5 h-5 ${s.color}`} />
                <Badge variant="outline" className="text-xs">{s.label}</Badge>
              </div>
              <p className={`text-2xl font-heading font-bold mt-2 ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Monthly chart */}
      <Card data-testid="monthly-chart-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-accent" /> Inscripciones e ingresos (últimos 6 meses)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }}
                  tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  formatter={(value, name) => name === 'Ingresos USD' ? fmtUSD(value) : value}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="enrollments" name="Inscripciones" fill="#1a3a5c" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="revenue_usd" name="Ingresos USD" fill="#c5a465" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Per-course breakdown */}
      <Card data-testid="per-course-breakdown">
        <CardHeader>
          <CardTitle className="text-base">Desempeño por curso</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {data.by_course.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No tienes cursos asignados todavía.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Curso</th>
                    <th className="text-right px-4 py-2 font-medium">Precio</th>
                    <th className="text-right px-4 py-2 font-medium">Estudiantes</th>
                    <th className="text-right px-4 py-2 font-medium">Aprobadas</th>
                    <th className="text-right px-4 py-2 font-medium">Pendientes</th>
                    <th className="text-right px-4 py-2 font-medium">Total recaudado</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_course.map(c => (
                    <tr key={c.course_id} className="border-t" data-testid={`course-row-${c.course_id}`}>
                      <td className="px-4 py-2 truncate max-w-xs">{c.title}</td>
                      <td className="px-4 py-2 text-right">{c.is_free ? <Badge variant="outline">Gratis</Badge> : fmtUSD(c.price)}</td>
                      <td className="px-4 py-2 text-right">{c.students}</td>
                      <td className="px-4 py-2 text-right">{c.approved_requests}</td>
                      <td className="px-4 py-2 text-right">{c.pending_requests}</td>
                      <td className="px-4 py-2 text-right font-semibold text-green-700">{fmtUSD(c.revenue_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfessorMetricsBlock;
